import React, { useState, useRef, useEffect } from "react";
import LoadingBar from "react-top-loading-bar";
import TopBar from "../../sections/TopBar";
import Sidebar from "../../sections/Sidebar";
import "../../customCss/ManageOfficers.css";
import DataService from "../../services/data.service";
import { toast } from "react-toastify";
import { Modal } from "react-bootstrap";
import NotFound from "../../images/no-data-found.jpeg";
import Spinner from "../../sections/Spinner";
import {
    TextField,
    Box,
    Grid,
    Button
} from "@mui/material";
import ContractorPopup from "./ContractorPopup";
import moment from "moment";
import * as XLSX from "xlsx";

const ContractorList = () => {
    const ref = useRef();
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [allContractors, setAllContractors] = useState([]);
    const [filteredContractors, setFilteredContractors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openForm, setOpenForm] = useState(false);
    const [selectedContractor, setSelectedContractor] = useState(null);
    const [deletePopup, setDeletePopup] = useState(false);
    const [btnLoading, setBtnLoading] = useState(false);
    const [viewMode, setViewMode] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        document.title = "Contractors || Admin Panel";
        window.scrollTo(0, 0);
        getAllContractors();

        const handleResize = () => {
            if (window.innerWidth < 768) setIsSidebarOpen(false);
        };
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

    const getAllContractors = () => {
        ref.current.continuousStart();
        setLoading(true);
        DataService.getContractors()
            .then((res) => {
                const data = res?.data?.data || [];
                setAllContractors(data.reverse());
                setFilteredContractors(data);
                ref.current.complete();
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
                toast.error("Failed to fetch contractors");
                ref.current.complete();
                setLoading(false);
            });
    };

    const handleSearch = (e) => {
        const query = e.target.value.toLowerCase();
        setSearchQuery(query);
        if (query) {
            const result = allContractors.filter((item) =>
                item.name?.toLowerCase().includes(query) ||
                item.email?.toLowerCase().includes(query) ||
                item.city?.toLowerCase().includes(query)
            );
            setFilteredContractors(result);
        } else {
            setFilteredContractors(allContractors);
        }
    };

    const handleAdd = () => {
        setSelectedContractor(null);
        setViewMode(false);
        setOpenForm(true);
    };

    const handleEdit = (item) => {
        setSelectedContractor(item);
        setViewMode(false);
        setOpenForm(true);
    };

    const handleView = (item) => {
        setSelectedContractor(item);
        setViewMode(true);
        setOpenForm(true);
    };

    const handleDelete = () => {
        if (!selectedContractor) return;
        setBtnLoading(true);
        DataService.deleteContractor(selectedContractor._id)
            .then(() => {
                toast.success("Contractor deleted successfully");
                setDeletePopup(false);
                getAllContractors();
            })
            .catch((err) => {
                const msg = err.response?.data?.message || "Failed to delete contractor";
                toast.error(msg);
            })
            .finally(() => {
                setBtnLoading(false);
            });
    };

    const handleFormSubmit = (formData) => {
        setBtnLoading(true);
        if (selectedContractor) {
            DataService.updateContractor(selectedContractor._id, formData)
                .then(() => {
                    toast.success("Contractor updated successfully");
                    setOpenForm(false);
                    getAllContractors();
                })
                .catch((err) => {
                    const msg = err.response?.data?.message || "Failed to update contractor";
                    toast.error(msg);
                })
                .finally(() => setBtnLoading(false));
        } else {
            DataService.createContractor(formData)
                .then(() => {
                    toast.success("Contractor added successfully");
                    setOpenForm(false);
                    getAllContractors();
                })
                .catch((err) => {
                    const msg = err.response?.data?.message || "Failed to add contractor";
                    toast.error(msg);
                })
                .finally(() => setBtnLoading(false));
        }
    };

    const exportToExcel = () => {
        if (filteredContractors.length === 0) {
            toast.error("No data available to export");
            return;
        }
        const formattedData = filteredContractors.map((item, index) => ({
            "S. No.": index + 1,
            Name: item.name || "-",
            Email: item.email || "-",
            Phone: item.phoneNo || "-",
            Address: `${item.contractorAddress || ""}, ${item.city || ""}, ${item.state || ""}, ${item.zipcode || ""}`,
            "Security Type": item.typeOfSecurity || "-",
            "Guards Required": item.noOfGuardRequired || "-",
            "Start Date": item.startDate ? moment(item.startDate).format("ll") : "-",
            "End Date": item.endDate ? moment(item.endDate).format("ll") : "-",
        }));

        const worksheet = XLSX.utils.json_to_sheet(formattedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Contractors");
        XLSX.writeFile(workbook, "Contractors_List.xlsx");
    };

    return (
        <>
            <LoadingBar color="#084e8a" ref={ref} height={5} />
            <div className={`main_layout ${isSidebarOpen ? "sidebar-open" : "sidebar-closed"}`}>
                <Sidebar isSidebarOpen={isSidebarOpen} />
                <div className="innerRight">
                    <TopBar title="Contractors" menu="" toggleSidebar={toggleSidebar} />
                    <div className="officersSec">
                        <div className="row align-items-center mb-3">
                            <div className="col-sm-6 col-6">
                                <h3 className="colorHeadThree mb-0">Contractors List</h3>
                            </div>
                            <div className="col-sm-6 col-6 text-right">
                                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                    <button className="btn btn-success" onClick={exportToExcel}>
                                        <i className="far fa-file-excel me-2" />
                                        Export
                                    </button>
                                    <button className="btn btn-primary" onClick={handleAdd}>
                                        <i className="fas fa-plus-circle me-2" />
                                        Add New
                                    </button>
                                </Box>
                            </div>
                        </div>

                        <Box sx={{ mb: 3, p: 2, bgcolor: "#f8f9fa", borderRadius: "8px" }}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Search Contractors"
                                        size="small"
                                        placeholder="Search by Name, Email, or City"
                                        sx={{ background: "#fff" }}
                                        value={searchQuery}
                                        onChange={handleSearch}
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        {filteredContractors.length > 0 ? (
                            <div className="tableScroller">
                                <table className="table table-striped">
                                    <thead style={{ whiteSpace: "nowrap" }}>
                                        <tr>
                                            <th scope="col">S. No.</th>
                                            <th scope="col">Name</th>
                                            <th scope="col">Email</th>
                                            <th scope="col">Phone</th>
                                            <th scope="col">City</th>
                                            <th scope="col">Security Type</th>
                                            <th scope="col">No. of Guards</th>
                                            <th scope="col">Contract End</th>
                                            <th scope="col" className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody style={{ whiteSpace: "nowrap" }}>
                                        {filteredContractors.map((item, i) => (
                                            <tr key={i}>
                                                <td>{i + 1}</td>
                                                <td>{item.name || "-"}</td>
                                                <td>{item.email || "-"}</td>
                                                <td>{item.phoneNo || "-"}</td>
                                                <td>{item.city || "-"}</td>
                                                <td style={{ textTransform: 'capitalize' }}>{item.typeOfSecurity || "-"}</td>
                                                <td className="text-center">{item.noOfGuardRequired || "-"}</td>
                                                <td>{item.endDate ? moment(item.endDate).format("ll") : "-"}</td>
                                                <td className="text-center">
                                                    <div className="tIco">
                                                        <i
                                                            className="fas fa-eye me-3"
                                                            onClick={() => handleView(item)}
                                                            title="View"
                                                            style={{ cursor: 'pointer' }}
                                                        />
                                                        <i
                                                            className="fas fa-pencil-alt me-3"
                                                            onClick={() => handleEdit(item)}
                                                            title="Edit"
                                                            style={{ cursor: 'pointer' }}
                                                        />
                                                        <i
                                                            className="fas fa-trash-alt me-3"
                                                            onClick={() => {
                                                                setSelectedContractor(item);
                                                                setDeletePopup(true);
                                                            }}
                                                            title="Delete"
                                                            style={{ cursor: 'pointer' }}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            !loading && (
                                <div className="text-center mt-4">
                                    <img src={NotFound} alt="noData" className="noData mb-2" />
                                    <p className="m-0">No Records Found.</p>
                                </div>
                            )
                        )}
                    </div>
                </div>
            </div>

            <ContractorPopup
                open={openForm}
                onClose={() => setOpenForm(false)}
                initialData={selectedContractor}
                onSubmit={handleFormSubmit}
                loading={btnLoading}
                readOnly={viewMode}
            />

            <Modal
                show={deletePopup}
                onHide={() => setDeletePopup(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Delete Contractor</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center">
                        <p className="mb-0">
                            Are you sure you want to delete contractor <strong>{selectedContractor?.name}</strong>?
                        </p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleDelete}
                        disabled={btnLoading}
                    >
                        {btnLoading ? <Spinner /> : "Delete"}
                    </button>
                    <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => setDeletePopup(false)}
                    >
                        Cancel
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ContractorList;
